import { useEffect, useState, useCallback } from "react";
import { Box, Flex, Stack, Text, HStack } from "@chakra-ui/react";
import { supabase } from "@/lib/supabaseClient";
import { FeatureToggle } from "@/components/FeatureToggle";
import { colors } from "@/theme/colors";

// Tier 1 — Super-Admin entitlement switches shown on each company card.
// Every change is audited immediately by a DB trigger and broadcast live
// to that company's signed-in users.
export const CompanyFeatures = ({ companyId, flash }) => {
  const [catalog, setCatalog] = useState([]);
  const [flags, setFlags] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    const [{ data: cat }, { data: rows }] = await Promise.all([
      supabase.from("feature_catalog").select("*").order("sort"),
      supabase.from("company_features").select("feature_key,enabled").eq("company_id", companyId),
    ]);
    setCatalog(cat || []);
    const map = {};
    (cat || []).forEach((f) => (map[f.key] = f.default_enabled));
    (rows || []).forEach((r) => (map[r.feature_key] = r.enabled));
    setFlags(map);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key) => {
    const next = !flags[key];
    setBusyKey(key);
    const { error } = await supabase
      .from("company_features")
      .upsert({ company_id: companyId, feature_key: key, enabled: next, updated_at: new Date().toISOString() });
    setBusyKey(null);
    if (error) { flash("error", error.message); return; }
    setFlags((f) => ({ ...f, [key]: next }));
    const label = catalog.find((c) => c.key === key)?.label || key;
    flash("success", `${label} turned ${next ? "ON" : "OFF"} for this company.`);
  };

  return (
    <Box mt="3" pt="3" borderTop="1px solid" borderColor={colors.border} data-testid={`features-section-${companyId}`}>
      <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color={colors.textSecondary} mb="2">
        Features (plan entitlements)
      </Text>
      <Stack gap="1.5">
        {catalog.map((f) => (
          <Flex key={f.key} align="center" justify="space-between" gap="2">
            <HStack gap="2" minW="0">
              <Text fontSize="sm" color={colors.textPrimary} data-testid={`feature-label-${companyId}-${f.key}`}>{f.label}</Text>
              {flags[f.key]
                ? <Text fontSize="10px" color={colors.sageDeep} fontWeight="600">ON</Text>
                : <Text fontSize="10px" color={colors.textSecondary} fontWeight="600">OFF</Text>}
            </HStack>
            <FeatureToggle
              on={Boolean(flags[f.key])}
              disabled={busyKey === f.key}
              onChange={() => toggle(f.key)}
              testId={`feature-toggle-${companyId}-${f.key}`}
            />
          </Flex>
        ))}
      </Stack>
    </Box>
  );
};
